import { Opportunity, OpportunityScore, Weights } from '../types/opportunity';

const assignTierColors = (sortedOpportunities: OpportunityScore[]): OpportunityScore[] => {
  // Calculate medians for value and feasibility scores
  const valueScores = sortedOpportunities.map(opp => opp.valueScore);
  const feasibilityScores = sortedOpportunities.map(opp => opp.feasibilityScore);
  
  const valueMedian = calculateMedian(valueScores);
  const feasibilityMedian = calculateMedian(feasibilityScores);
  
  console.log('Medians calculated:', { valueMedian, feasibilityMedian });

  // Assign colors based on quadrants
  return sortedOpportunities.map(opp => ({
    ...opp,
    quadrantColor: determineQuadrantColor(opp, valueMedian, feasibilityMedian)
  }));
};

const calculateMedian = (numbers: number[]): number => {
  const sorted = [...numbers].sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);
  
  if (sorted.length % 2 === 0) {
    return (sorted[middle - 1] + sorted[middle]) / 2;
  }
  
  return sorted[middle];
};

const determineQuadrantColor = (
  opp: OpportunityScore,
  valueMedian: number,
  feasibilityMedian: number
): string => {
  const isHighValue = opp.valueScore > valueMedian;
  const isHighFeasibility = opp.feasibilityScore > feasibilityMedian;

  if (isHighValue && isHighFeasibility) return "#CA0079"; // Top Right - Most Valuable + Most Feasible
  if (isHighValue && !isHighFeasibility) return "#3B50A2"; // Top Left - Most Valuable + Less Feasible
  if (!isHighValue && !isHighFeasibility) return "#F1AB11"; // Bottom Left - Less Valuable + Less Feasible
  return "#64748b"; // Bottom Right - Less Valuable + More Feasible
};

export const calculateScores = (opportunities: Opportunity[], weights: Weights): OpportunityScore[] => {
  console.log('Starting score calculations with weights:', weights);

  const valueDimensions = [
    'SpeedUpPermitting',
    'IncreaseStaffProductivity',
    'SimplifyApplicationExperience',
    'EnhanceEquitableAccessToServices',
    'StrengthenInternalVisibilityDecisionMaking',
    'IncreaseProcessTransparency',
    'BoostFinancialSustainability'
  ];

  const feasibilityFactors = [
    'CostAndEffort',
    'Risk',
    'Complexity'
  ];

  let scoredOpportunities = opportunities.map(opp => {
    // Calculate raw value score
    const valueSum = valueDimensions.reduce((sum, dim) => {
      const score = Number(opp[dim as keyof Opportunity]) || 0;
      const weight = Number(weights[dim as keyof Weights]) || 0;
      return sum + (score * weight);
    }, 0);

    // Calculate maximum possible value score
    const maxValueSum = valueDimensions.reduce((sum, dim) => {
      const weight = Number(weights[dim as keyof Weights]) || 0;
      return sum + (5 * weight);
    }, 0);

    // Calculate raw feasibility score (inverse of challenges)
    const feasibilitySum = feasibilityFactors.reduce((sum, factor) => {
      const score = Number(opp[factor as keyof Opportunity]) || 0;
      const weight = Number(weights[factor as keyof Weights]) || 0;
      return sum + (score * weight);
    }, 0);

    // Calculate maximum possible feasibility score
    const maxFeasibilitySum = feasibilityFactors.reduce((sum, factor) => {
      const weight = Number(weights[factor as keyof Weights]) || 0;
      return sum + (5 * weight);
    }, 0);

    // Normalize scores to 0-1 scale
    const valueScore = maxValueSum > 0 ? valueSum / maxValueSum : 0;
    const feasibilityScore = maxFeasibilitySum > 0 ? 1 - (feasibilitySum / maxFeasibilitySum) : 0;

    // Calculate final score (50/50 weight between value and feasibility)
    const finalScore = (valueScore * 0.5) + (feasibilityScore * 0.5);

    console.log(`Scored ${opp.Name}:`, {
      valueScore: valueScore.toFixed(3),
      feasibilityScore: feasibilityScore.toFixed(3),
      finalScore: finalScore.toFixed(3)
    });

    return {
      ...opp,
      valueScore,
      feasibilityScore,
      finalScore,
      quadrantColor: '' // Placeholder for tier color
    };
  });

  // Sort by final score and assign colors based on quadrants
  scoredOpportunities = assignTierColors(scoredOpportunities);

  return scoredOpportunities;
};